import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/EcoAppsAuthContext';
import { getLogoutRedirectPath } from '../../services/demoSession';
import {
  Menu,
  Bell,
  Search,
  ChevronDown,
  User,
  Settings,
  LogOut,
  CheckCircle2,
  Clock3,
} from 'lucide-react';

const Header = ({ onMenuClick, sidebarCollapsed }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAgendaLab = location.pathname.startsWith('/agendalab');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);
  const searchRef = useRef(null);
  const notificationsAbortRef = useRef(null);
  const brainNotificationsEnabled = false;

  const unreadCount = notifications.filter((n) => !n.read).length;

  const loadNotifications = async () => {
    if (!brainNotificationsEnabled) {
      notificationsAbortRef.current?.abort();
      setNotifications([]);
      setLoadingNotifications(false);
      return;
    }

    notificationsAbortRef.current?.abort();
    const controller = new AbortController();
    notificationsAbortRef.current = controller;

    try {
      setLoadingNotifications(true);
      const response = await fetch('/api/brain/invitations', { credentials: 'include', signal: controller.signal });
      if (!response.ok) {
        setNotifications([]);
        return;
      }

      const payload = await response.json();
      setNotifications((payload?.invitations || []).slice(0, 6).map((invitation) => ({
        id: invitation.id,
        title: invitation.status === 'Pending' ? 'Invitacion a room' : 'Actualizacion de invitacion',
        message: `${invitation.senderName} te invito a ${invitation.roomName}`,
        time: invitation.createdAtLabel,
        read: invitation.status !== 'Pending',
        href: invitation.status === 'Accepted' ? `/brain-emprendedor/rooms/${invitation.roomId}` : '/brain-emprendedor/invitations',
        icon: invitation.status === 'Accepted' ? CheckCircle2 : Clock3,
        iconColor: invitation.status === 'Accepted' ? 'text-emerald-500' : 'text-amber-500',
        bgColor: invitation.status === 'Accepted' ? 'bg-emerald-100' : 'bg-amber-100',
      })));
    } catch {
      if (!controller.signal.aborted) setNotifications([]);
    } finally {
      if (!controller.signal.aborted) setLoadingNotifications(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) setShowNotifications(false);
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setShowUserMenu(false);
      if (searchRef.current && !searchRef.current.contains(event.target)) setShowSearch(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!user || !brainNotificationsEnabled) {
      setNotifications([]);
      setLoadingNotifications(false);
      return undefined;
    }

    loadNotifications();
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadNotifications();
      }
    }, 30000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadNotifications();
      }
    };

    window.addEventListener('focus', loadNotifications);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      notificationsAbortRef.current?.abort();
      window.clearInterval(intervalId);
      window.removeEventListener('focus', loadNotifications);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, location.pathname]);

  const userInitials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`;
  const userFullName = `${user?.first_name || ''} ${user?.last_name || ''}`;
  const userRole = user?.role === 'landlord' ? 'Arrendador' : 'Arrendatario';
  const roleLabel = user?.rentalBizAssignedMode === 'both' ? `${userRole} (Ambos)` : userRole;

  const handleLogout = async () => {
    const redirectPath = getLogoutRedirectPath(user, location.pathname);
    await logout();
    window.location.href = redirectPath;
  };

  const openNotificationTarget = (href) => {
    setShowNotifications(false);
    navigate(href);
  };

  return (
    <header className={`
      sticky top-0 z-30 bg-white shadow-soft
      transition-all duration-300
      ${sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'}
    `}>
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        <div className="flex items-center space-x-4">
          <button
            className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            onClick={onMenuClick}
            aria-label="Abrir menú"
          >
            <Menu className="w-6 h-6" />
          </button>

          {!isAgendaLab && (
            <>
              <div className="hidden md:block relative" ref={searchRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar propiedades, contratos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-64 lg:w-80 pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-150"
                  />
                </div>
              </div>

              <button
                className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                onClick={() => setShowSearch(!showSearch)}
                aria-label="Buscar"
              >
                <Search className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        <div className="flex items-center space-x-3">
          <div className="hidden lg:block text-right">
            <p className="text-sm font-medium text-gray-900">{userFullName}</p>
            {!isAgendaLab && <p className="text-xs text-gray-500 capitalize">{roleLabel}</p>}
          </div>

          <div className="relative" ref={notificationRef}>
            <button
              className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
              onClick={() => setShowNotifications(!showNotifications)}
              aria-label="Notificaciones"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden animate-slide-down">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">Notificaciones</h3>
                  <button
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                    onClick={() => openNotificationTarget('/brain-emprendedor/invitations')}
                  >
                    Ver invitaciones
                  </button>
                </div>

                <div className="max-h-80 overflow-y-auto">
                  {loadingNotifications ? (
                    <div className="px-4 py-6 text-center">
                      <p className="text-sm text-gray-500">Cargando notificaciones...</p>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-6 text-center">
                      <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No hay notificaciones</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <button
                        key={notification.id}
                        type="button"
                        className={`w-full px-4 py-3 border-b border-gray-50 hover:bg-gray-50 text-left transition-colors duration-150 ${!notification.read ? 'bg-primary-50/30' : ''}`}
                        onClick={() => openNotificationTarget(notification.href)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`p-2 rounded-lg ${notification.bgColor}`}>
                            <notification.icon className={`w-4 h-4 ${notification.iconColor}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notification.message}</p>
                            <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                          </div>
                          {!notification.read && <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0 mt-2" />}
                        </div>
                      </button>
                    ))
                  )}
                </div>

                <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
                  <button
                    className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
                    onClick={() => openNotificationTarget('/brain-emprendedor/invitations')}
                  >
                    Ver todas las notificaciones
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={userMenuRef}>
            <button
              className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label="Menú de usuario"
            >
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                {userInitials ? <span className="text-xs font-semibold text-primary-600">{userInitials}</span> : <User className="w-4 h-4 text-primary-600" />}
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden animate-slide-down">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{userFullName}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>

                <div className="py-1">
                  <button className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors" onClick={() => setShowUserMenu(false)}>
                    <User className="w-4 h-4 mr-3 text-gray-400" />
                    Mi perfil
                  </button>
                  <button className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors" onClick={() => setShowUserMenu(false)}>
                    <Settings className="w-4 h-4 mr-3 text-gray-400" />
                    Configuracion
                  </button>
                </div>

                <div className="border-t border-gray-100 py-1">
                  <button className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors" onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-3" />
                    Cerrar sesion
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {!isAgendaLab && showSearch && (
        <div className="md:hidden px-4 pb-3 animate-slide-down">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              autoFocus
            />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
